import { useEffect, useMemo, useRef, useState } from 'react';
import { Board } from '@/chess/board';
import { KidHint, TurnIndicator } from '@/ui';
import type { DrillPosition, RepertoireDetail } from './types';
import { openingLabApi, type AttemptPayload } from './api';
import { mapPlanToGeneric } from './planMapping';
import { pickUnseenDeepPosition } from './drillPickers';

interface Props {
  repertoire: RepertoireDetail;
  positions: DrillPosition[];
  onResult: (result: 'success' | 'partial' | 'fail') => void;
}

/**
 * Mode `survival` : Léon est hors-théorie (position random profonde du PGN
 * où aucune mainline parfaite n'est définie). On lui demande de choisir un
 * plan générique parmi 4 — celui qui correspond au commentaire `#plan` le
 * plus proche dans la mainline est marqué comme "acceptable".
 *
 * MVP1.0 : version simplifiée — on fait un mapping mots-clés des #plan vers
 * les 4 plans génériques. Les autres réponses sont marquées partial (pas fail
 * net : Stockfish doit valider, non disponible côté front en mode batch V1).
 */
const GENERIC_PLANS = [
  'Développer rapidement les pièces mineures et roquer',
  'Contrôler le centre avec les pions',
  'Attaquer le roi adverse sur l\'aile-roi',
  'Échanger les fous pour simplifier la position',
] as const;

export function DrillSurvival({ repertoire, positions, onResult }: Props) {
  // Fix 2026-05-17 : Set in-memory des positions déjà drillées pour éviter
  // les répétitions consécutives ("ce sont souvent les mêmes exercices"
  // — bug Léon). Clé `fenBefore` (unique par position du PGN).
  const seenRef = useRef<Set<string>>(new Set());
  const [position, setPosition] = useState<DrillPosition>(() => pickUnseenDeepPosition(positions, seenRef.current));
  const [phase, setPhase] = useState<'playing' | 'revealed'>('playing');
  const [picked, setPicked] = useState<number | null>(null);
  const startedAtRef = useRef<number>(performance.now());

  useEffect(() => {
    startedAtRef.current = performance.now();
    setPhase('playing');
    setPicked(null);
  }, [position]);

  const acceptableIdx = useMemo(() => mapPlanToGeneric(position.tags.plan), [position]);

  function logAttempt(result: AttemptPayload['result']) {
    const payload: AttemptPayload = {
      module: 'opening',
      slug: `${repertoire.slug}:survival-ply${position.ply}`,
      fen: position.fenBefore,
      side_to_move: position.sideToMove,
      mode: 'survival',
      result,
      time_spent_ms: Math.round(performance.now() - startedAtRef.current),
      source_ref: repertoire.slug,
    };
    openingLabApi.postAttempt(payload).catch((e) => console.error('[survival] log failed', e));
    onResult(result);
  }

  function pick(idx: number) {
    if (phase === 'revealed') return;
    setPicked(idx);
    setPhase('revealed');
    if (acceptableIdx === null) {
      // Pas assez de signal — on log en partial.
      logAttempt('partial');
      return;
    }
    logAttempt(idx === acceptableIdx ? 'success' : 'fail');
  }

  function next() {
    setPosition(pickUnseenDeepPosition(positions, seenRef.current));
  }

  return (
    <div className="flex flex-col gap-4">
      <KidHint tone="violet" title="Pourquoi ce drill ?">
        Quand tu sors de ta théorie, tu ne dois pas paniquer. Tu choisis un
        plan générique cohérent avec la position — pas un coup parfait, juste
        un coup raisonnable.
      </KidHint>
      <header className="flex items-baseline justify-between">
        <p className="text-caption uppercase text-violet-500">Tiens bon · coup {position.ply} · {position.sideToMove === 'white' ? 'Blancs' : 'Noirs'}</p>
        <p className="text-body-sm text-ink-300">Hors-théorie. Quel plan privilégier ?</p>
      </header>

      <TurnIndicator
        state={phase === 'playing' ? 'user-turn' : 'idle'}
        userLabel="À toi de choisir le bon plan"
        idleLabel="Plan révélé"
      />

      <Board
        fen={position.fenBefore}
        orientation={repertoire.color}
        interactive={false}
        turnColor={position.sideToMove}
      />

      <section className="flex flex-col gap-2">
        {GENERIC_PLANS.map((plan, i) => (
          <button
            key={plan}
            type="button"
            onClick={() => pick(i)}
            disabled={phase === 'revealed'}
            className={
              'rounded-lg border px-4 py-3 text-left text-body transition ' +
              (phase === 'revealed' && acceptableIdx === i
                ? 'border-volt-500 bg-volt-500/10 text-volt-500'
                : phase === 'revealed' && picked === i && acceptableIdx !== null && i !== acceptableIdx
                ? 'border-coral-500 bg-coral-500/10 text-coral-500'
                : phase === 'revealed' && picked === i && acceptableIdx === null
                ? 'border-amber-500 bg-amber-500/10 text-amber-500'
                : 'border-ink-700 bg-ink-800 text-paper-50 hover:border-violet-500')
            }
          >
            {plan}
          </button>
        ))}
      </section>

      {phase === 'revealed' && (
        <>
          {acceptableIdx === null && (
            <p className="text-body-sm text-amber-500">
              Position hors-livre — aucune référence stricte. Tentative notée comme partielle.
            </p>
          )}
          {position.tags.plan && (
            <p className="rounded-xl border border-ink-700 bg-ink-800 p-3 text-body-sm text-ink-300">
              <strong className="text-paper-50">Plan référent :</strong> {position.tags.plan}
            </p>
          )}
          <button
            type="button"
            onClick={next}
            className="self-start rounded-lg bg-violet-500 px-4 py-2 font-display text-body text-paper-50 transition hover:opacity-90"
          >
            Position suivante
          </button>
        </>
      )}
    </div>
  );
}

